import { useEffect, useState } from 'react';
import { PageShell } from '../components';
import { PageBody, PageHero, Section, CardGrid } from '../components/page';
import { SIGNUP_URL, BREW_APP, BREW_CLI } from '../lib/appUrls';
import { CopyCommand } from '../components/CopyCommand';

const BUCKET = 'https://storage.googleapis.com/statio-downloads/latest';
const MANIFEST = `${BUCKET}/latest.json`;

// The release pipeline publishes every build under a fixed name in latest/, so
// the links are known at build time and are prerendered: the page works with
// JavaScript off, and when the manifest cannot be fetched (the bucket has no
// CORS policy for this origin, which left the live page with no buttons at
// all). The manifest only adds the version number, and its URLs win if they
// ever differ from these.
const FALLBACK: Record<string, string> = {
  'macos-arm64': `${BUCKET}/Statio-macos-apple-silicon.dmg`,
  'macos-x64': `${BUCKET}/Statio-macos-intel.dmg`,
  'windows-msi': `${BUCKET}/Statio-windows-x86_64.msi`,
  'windows-exe': `${BUCKET}/Statio-windows-x86_64-setup.exe`,
  'linux-appimage': `${BUCKET}/Statio-linux-x86_64.AppImage`,
  'linux-deb': `${BUCKET}/Statio-linux-x86_64.deb`,
};

// Apple Silicon and Intel are separate builds, and picking the wrong one fails
// in a way that reads as a broken app rather than a wrong download. Detection
// picks the likely default; every option stays visible so nobody is stuck.
function detectPlatform(): string | null {
  if (typeof navigator === 'undefined') return null;

  const ua = navigator.userAgent;
  const platform = navigator.platform || '';

  if (/Win/i.test(ua)) return 'windows-msi';
  if (/Linux/i.test(ua) && !/Android/i.test(ua)) return 'linux-appimage';

  if (/Mac/i.test(ua) || /Mac/i.test(platform)) {
    // Apple Silicon reports as Intel under Rosetta and in most UA strings, so
    // this cannot be read off the UA. When unsure default to Apple Silicon. It
    // is the far more common machine now, and the page shows both regardless.
    return 'macos-arm64';
  }

  return null;
}

const LABELS: Record<string, { name: string; short: string; note: string }> = {
  'macos-arm64': { name: 'macOS (Apple Silicon)', short: 'Apple Silicon', note: 'M1 and later' },
  'macos-x64': { name: 'macOS (Intel)', short: 'Intel', note: 'pre-2020 Macs' },
  'windows-msi': { name: 'Windows', short: 'MSI', note: '.msi installer' },
  'windows-exe': { name: 'Windows', short: 'EXE', note: '.exe installer' },
  'linux-appimage': { name: 'Linux (AppImage)', short: 'AppImage', note: 'runs on most distributions' },
  'linux-deb': { name: 'Linux (.deb)', short: '.deb', note: 'Debian and Ubuntu' },
};

const GROUPS: { title: string; keys: string[] }[] = [
  { title: 'macOS', keys: ['macos-arm64', 'macos-x64'] },
  { title: 'Windows', keys: ['windows-msi', 'windows-exe'] },
  { title: 'Linux', keys: ['linux-appimage', 'linux-deb'] },
];

type Manifest = { version?: string; platforms?: Record<string, string> };

export default function DownloadPage() {
  const [manifest, setManifest] = useState<Manifest | null>(null);
  // Detected after hydration so the prerendered HTML and the first client
  // render agree; the platform cards are there either way.
  const [detected, setDetected] = useState<string | null>(null);

  useEffect(() => {
    setDetected(detectPlatform());
    fetch(MANIFEST)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then(setManifest)
      .catch(() => {});
  }, []);

  const platforms = { ...FALLBACK, ...(manifest?.platforms ?? {}) };
  const primary = detected && platforms[detected] ? detected : null;

  // Download files live off-site on Cloud Storage, so these are plain anchors.
  const cards = GROUPS.map((g) => {
    const builds = g.keys.filter((k) => platforms[k]);
    return {
      title: g.title,
      body:
        builds.length > 0 ? (
          <p>{builds.map((k) => `${LABELS[k].short}: ${LABELS[k].note}`).join('. ')}.</p>
        ) : (
          <p>No build listed right now.</p>
        ),
      extra:
        builds.length > 0 ? (
          <div className="pg-actions">
            {builds.map((k) => (
              <a key={k} className={k === primary ? 's-btn s-btn-pri' : 's-btn s-btn-sec'} href={platforms[k]}>
                Download {LABELS[k].short}
              </a>
            ))}
          </div>
        ) : undefined,
    };
  });

  return (
    <PageShell>
      <PageBody>
        <PageHero
          title="Download Statio"
          lede="Statio needs the desktop app on every machine that runs an AI client. It connects that machine to Statio, detects which MCP clients you already have, writes their configuration, and finds servers already set up on your machine that nobody governs."
          actions={
            primary ? (
              <a className="s-btn s-btn-pri" href={platforms[primary]}>
                Download for {LABELS[primary].name}
              </a>
            ) : undefined
          }
        >
          <p className="pg-hero-note">
            After installing, sign in with your Statio account.{' '}
            <a href={SIGNUP_URL} target="_blank" rel="noopener noreferrer">
              Create a free account
            </a>{' '}
            if you don’t have one.
          </p>
          {manifest?.version && <p>Version {manifest.version}</p>}
        </PageHero>

        <Section id="platforms" title={primary ? 'All platforms' : 'Choose your platform'}>
          <CardGrid items={cards} />
        </Section>

        <Section
          id="homebrew"
          title="Install with Homebrew"
          lede="The desktop app on macOS, and the mcphub CLI on its own for macOS or Linux."
        >
          <p>Desktop app (macOS):</p>
          <CopyCommand text={BREW_APP} />
          <p style={{ marginTop: 24 }}>mcphub CLI (macOS and Linux):</p>
          <CopyCommand text={BREW_CLI} />
        </Section>

        <Section id="which-mac" title="Not sure which Mac you have?" split>
          <p>
            Apple menu → About This Mac. A chip named M1, M2, M3 or M4 is Apple Silicon. Anything saying Intel is the
            Intel build.
          </p>
        </Section>
      </PageBody>
    </PageShell>
  );
}
